import React, { useEffect, useMemo, useRef } from 'react';
import styles from './index.module.scss';
import { MinimalisticArrowIcon } from '../../assets/images/icons';
import {
  checkIfAnswerIsSchedule,
  generateDummyAvailability,
  getHorizontalScrollPercent,
  getOffsetFromDate,
  getWeekDays,
  isSameDay,
  isSameTimeSlot,
} from './helpers';
import { useScheduleAvailability } from '../../services/api/hooks/use-schedule-availability';
import DayButton from './DayButton';
import { FormsLoadClientDataStepsInputsScheduleDto } from '../../services/api/types/forms.responses';
import Spinner from '../Spinner';
import {
  ScheduleGetAvailabilityResponseDto,
  ScheduleTimeSlot,
} from '../../services/api/types/schedule';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionRemoveAnimatingComponents,
  actionUpdateAnimatingComponents,
} from '../../store/actions/ui.actions';
import {
  getAnimatingComponents,
  getFormStepAnswers,
  getPreviousSteps,
} from '../../store/selectors';
import { useDeviceType } from '../../hooks/use-device-type';

interface ScheduleInputProps {
  formToken: string | null;
  inputId: number;
  schedule: FormsLoadClientDataStepsInputsScheduleDto;
  className?: string;
  defaultValue?: ScheduleTimeSlot | null;
  name?: string;
  onSelect?: (timeSlot: ScheduleTimeSlot | null) => void | Promise<void>;
  initialDelay?: number;
  isDemo?: boolean;
}

function ScheduleInput({
  className = '',
  formToken,
  inputId,
  schedule,
  onSelect,
  initialDelay = 0,
  defaultValue = null,
  isDemo = false,
}: ScheduleInputProps) {
  const deviceType = useDeviceType();
  const dateSelectorItemsRef = useRef<HTMLDivElement>(null);
  const dateLimit = deviceType !== 'mobile' ? 7 : 14;
  const maxBookingsPerSlot = schedule?.maxBookingsPerSlot || 1;
  const [selectedTimeSlot, setSelectedTimeSlot] =
    React.useState<ScheduleTimeSlot | null>(defaultValue);
  const [dateOffset, setDateOffset] = React.useState<number>(
    defaultValue?.startTime
      ? getOffsetFromDate(new Date(defaultValue.startTime), dateLimit)
      : 0,
  );
  const [currentDate, setCurrentDate] = React.useState<Date>(
    defaultValue?.startTime ? new Date(defaultValue.startTime) : new Date(),
  );
  const [mobileMonthDate, setMobileMonthDate] =
    React.useState<Date>(currentDate);
  const dispatch = useDispatch();
  const componentsAnimating = useSelector(getAnimatingComponents);
  const initialDelayTimeSlots = useRef(initialDelay + 0.35);

  const allStepAnswers = useSelector(getFormStepAnswers);
  const previousSteps = useSelector(getPreviousSteps);
  const alreadySelectedSlots = useMemo(() => {
    const selectedSlots: ScheduleTimeSlot[] = [];
    if (allStepAnswers) {
      Object.keys(allStepAnswers).forEach((stepId) => {
        if (previousSteps && !previousSteps.includes(Number(stepId))) {
          return;
        }
        const answers = allStepAnswers[stepId as any].answers;
        Object.keys(answers)
          .filter((_inputId) => inputId !== Number(_inputId))
          .forEach((answerKey) => {
            const answer = answers[answerKey as any];
            if (checkIfAnswerIsSchedule(answer)) {
              selectedSlots.push(answer.value as ScheduleTimeSlot);
            }
          });
      });
    }
    return selectedSlots;
  }, [allStepAnswers]);

  const [availabilityRequestData, availabilityQueryData, weekDays] =
    useMemo(() => {
      const startDate = new Date();
      const startDateCP = new Date(startDate);
      startDateCP.setDate(startDate.getDate() + dateOffset);
      const useCached = deviceType === 'mobile';
      const weekDays = useCached
        ? getWeekDays(startDate, dateOffset + dateLimit)
        : getWeekDays(startDateCP, dateLimit);
      return [
        {
          inputId,
          dayDate: startDate.toISOString(),
          limit: useCached ? dateLimit : dateLimit + 7,
          offset: dateOffset,
        },
        {
          form: formToken || '',
        },
        weekDays,
      ];
    }, [dateOffset, deviceType]);

  function getTimeSlotsForDate(
    date: Date,
    res?: ScheduleGetAvailabilityResponseDto,
  ) {
    return (
      (res || data)?.availability?.find((availabilityDate) =>
        isSameDay(new Date(availabilityDate.date), date),
      )?.times || []
    );
  }

  const { data, ready } = isDemo
    ? {
        data: generateDummyAvailability(),
        ready: true,
      }
    : useScheduleAvailability(
        availabilityRequestData,
        availabilityQueryData,
        {
          onLoad: (res, isFirstTime) => {
            const useCached = deviceType === 'mobile';
            if (useCached && !isFirstTime) {
              return;
            }
            if (res?.availability) {
              if (selectedTimeSlot) {
                const foundDate = res.availability.find((dateAvailability) =>
                  dateAvailability.times.find((timeSlot) =>
                    isSameTimeSlot(timeSlot, selectedTimeSlot),
                  ),
                );
                if (foundDate) {
                  setCurrentDate(new Date(foundDate.date));
                  return;
                }
              }
              weekDays.find((day) => {
                const timeSlots = getTimeSlotsForDate(day, res);
                if (timeSlots.length > 0) {
                  setCurrentDate(day);
                  return true;
                }
                return false;
              });
            }
          },
        },
        deviceType === 'mobile',
      );

  async function handlePrevMonthClick() {
    if (dateOffset - dateLimit >= 0) {
      setDateOffset(dateOffset - dateLimit);
      setCurrentDate((prev) => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() - dateLimit);
        return newDate;
      });
    }
  }

  async function handleNextMonthClick() {
    setDateOffset(dateOffset + dateLimit);
    if (deviceType !== 'mobile') {
      setCurrentDate((prev) => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + dateLimit);
        return newDate;
      });
    }
  }

  const currentDateTimesAvailable = getTimeSlotsForDate(currentDate);
  const monthDisplay = (
    deviceType === 'mobile' ? mobileMonthDate : currentDate
  ).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  useEffect(() => {
    function onHorizontalScroll(e: Event) {
      if (dateSelectorItemsRef.current) {
        const scrolledPercent = getHorizontalScrollPercent(
          dateSelectorItemsRef.current,
        );
        const trigger = 80;
        if (scrolledPercent >= trigger) {
          handleNextMonthClick();
        }

        // Calculate which month to show based on scroll position
        const oneDayElement = dateSelectorItemsRef.current.childNodes?.[0];
        const oneDayGap = 12;
        const oneDayWidth = oneDayElement
          ? (oneDayElement as HTMLElement).offsetWidth + oneDayGap
          : 0;
        const totalScrollLeft = dateSelectorItemsRef.current.scrollLeft;
        const daysScrolled = Math.round(totalScrollLeft / oneDayWidth);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() + daysScrolled);
        setMobileMonthDate(startDate);
      }
    }
    if (deviceType === 'mobile' && dateSelectorItemsRef.current) {
      dateSelectorItemsRef.current.addEventListener(
        'scroll',
        onHorizontalScroll,
      );
    }
    return () => {
      if (deviceType === 'mobile' && dateSelectorItemsRef.current) {
        dateSelectorItemsRef.current.removeEventListener(
          'scroll',
          onHorizontalScroll,
        );
      }
    };
  }, [deviceType, data?.availability]);

  return (
    <div className={`${styles.scheduleInput} ${className}`}>
      <div className={styles.holder}>
        <motion.div
          className={styles.top}
          initial={{
            opacity: 0,
            translateY: '15%',
          }}
          onLoad={() => {
            dispatch(
              actionUpdateAnimatingComponents({
                components: ['scheduleNav'],
              }),
            );
          }}
          onAnimationComplete={() => {
            if (componentsAnimating.includes('schedule')) {
              dispatch(
                actionRemoveAnimatingComponents({
                  components: ['scheduleNav'],
                }),
              );
            }
          }}
          animate={{
            opacity: 1,
            translateY: '0%',
            transition: {
              duration: 0.7,
              delay: initialDelay,
              ease: 'anticipate',
            },
          }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0.4,
              delay: 0,
              ease: 'anticipate',
            },
            pointerEvents: 'none',
          }}
        >
          <span className={styles.label}>{monthDisplay}</span>
          <div className={styles.navButtons}>
            <button
              className={styles.navButton}
              aria-label="Previous month"
              onClick={handlePrevMonthClick}
              disabled={dateOffset === 0}
            >
              <MinimalisticArrowIcon className={styles.arrowIconLeft} />
            </button>
            <button
              className={styles.navButton}
              aria-label="Next month"
              onClick={handleNextMonthClick}
            >
              <MinimalisticArrowIcon className={styles.arrowIconRight} />
            </button>
          </div>
        </motion.div>
        <motion.div
          className={styles.dateSelector}
          ref={dateSelectorItemsRef}
          initial={{
            opacity: 0,
            translateY: '15%',
          }}
          onLoad={() => {
            dispatch(
              actionUpdateAnimatingComponents({
                components: ['scheduleDateSelector'],
              }),
            );
          }}
          onAnimationComplete={() => {
            if (componentsAnimating.includes('scheduleDateSelector')) {
              dispatch(
                actionRemoveAnimatingComponents({
                  components: ['scheduleDateSelector'],
                }),
              );
            }
          }}
          animate={{
            opacity: 1,
            translateY: '0%',
            transition: {
              duration: 0.7,
              delay: initialDelay,
              ease: 'anticipate',
            },
          }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0.4,
              delay: 0,
              ease: 'anticipate',
            },
            pointerEvents: 'none',
          }}
        >
          {weekDays.map((day, index) => {
            return (
              <DayButton
                className={styles.dateSelectorDay}
                key={day.toISOString()}
                day={day}
                activeDay={currentDate}
                availability={getTimeSlotsForDate(day)}
                onClick={async (selectedDay) => {
                  setCurrentDate(selectedDay);
                }}
                // showMonth={
                //   deviceType === 'mobile' &&
                //   ((day.getDate() === 1 && index > 2) || index === 0)
                // }
              />
            );
          })}
        </motion.div>
        <div className={styles.timeSlots}>
          <motion.div
            className={styles.timeSlotsHeader}
            initial={{
              opacity: 0,
              translateY: '15%',
            }}
            onLoad={() => {
              dispatch(
                actionUpdateAnimatingComponents({
                  components: ['timeSlotsHeader'],
                }),
              );
            }}
            onAnimationComplete={() => {
              if (componentsAnimating.includes('timeSlotsHeader')) {
                dispatch(
                  actionRemoveAnimatingComponents({
                    components: ['timeSlotsHeader'],
                  }),
                );
              }
              initialDelayTimeSlots.current = 0;
            }}
            animate={{
              opacity: 1,
              translateY: '0%',
              transition: {
                duration: 0.7,
                delay: initialDelay,
                ease: 'anticipate',
              },
            }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.4,
                delay: 0,
                ease: 'anticipate',
              },
              pointerEvents: 'none',
            }}
          >
            <span className={styles.timeSlotsHeaderLabel}>Select time</span>
          </motion.div>
          <div className={styles.timeSlotsListLoading}>
            {/* <AnimatePresence mode="wait"> */}
            {!ready ? (
              <motion.div
                key="loading"
                className={styles.loading}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { duration: 0.25, ease: 'easeIn' },
                }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0.15, ease: 'easeOut' },
                }}
              >
                <Spinner color="var(--title-color)" />
              </motion.div>
            ) : (
              <div className={styles.timeSlotsListItems} key="list">
                {currentDateTimesAvailable?.length ? (
                  currentDateTimesAvailable
                    .filter((timeSlotAvailable) => {
                      const countOfExistingOverlaps =
                        alreadySelectedSlots.filter((selectedSlot) =>
                          isSameTimeSlot(selectedSlot, timeSlotAvailable),
                        ).length;
                      return countOfExistingOverlaps < maxBookingsPerSlot;
                    })
                    .map((timeSlot, index) => {
                      const startTime = new Date(
                        timeSlot.startTime,
                      ).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                      const timeSlotActive =
                        selectedTimeSlot &&
                        isSameTimeSlot(timeSlot, selectedTimeSlot);
                      return (
                        <motion.button
                          key={index}
                          className={`${styles.timeSlot} ${
                            timeSlotActive ? styles.active : ''
                          }`}
                          onClick={() => {
                            const value: ScheduleTimeSlot | null =
                              timeSlotActive ? null : timeSlot;
                            setSelectedTimeSlot(value);
                            if (onSelect) {
                              onSelect(value);
                            }
                          }}
                          initial={{
                            opacity: 0,
                            translateY: '15%',
                          }}
                          onLoad={() => {
                            dispatch(
                              actionUpdateAnimatingComponents({
                                components: ['timeSlots'],
                              }),
                            );
                          }}
                          onAnimationComplete={() => {
                            if (componentsAnimating.includes('timeSlots')) {
                              dispatch(
                                actionRemoveAnimatingComponents({
                                  components: ['timeSlots'],
                                }),
                              );
                            }
                          }}
                          animate={{
                            opacity: 1,
                            translateY: '0%',
                            transition: {
                              duration: 0.7 - index * 0.05 >= 0 ? 0.15 : 0.15,
                              delay:
                                initialDelayTimeSlots.current + index * 0.05,
                              ease: 'anticipate',
                            },
                          }}
                          exit={{
                            opacity: 0,
                            transition: {
                              duration: 0.4,
                              delay: 0,
                              ease: 'anticipate',
                            },
                            pointerEvents: 'none',
                          }}
                        >
                          {startTime}
                        </motion.button>
                      );
                    })
                ) : (
                  <motion.div
                    className={styles.noAvailability}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: { duration: 0.25, ease: 'easeIn' },
                    }}
                    exit={{
                      opacity: 0,
                      transition: { duration: 0.15, ease: 'easeOut' },
                    }}
                  >
                    No availability for selected date
                  </motion.div>
                )}
              </div>
            )}
            {/* </AnimatePresence> */}
          </div>
        </div>
      </div>
    </div>
  );
}
export default ScheduleInput;
